import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ChevronRight, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { useLocale } from "@/hooks/useLocale";
import { usePermissions } from "@/hooks/usePermissions";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import { PermissionLevelEnum, type PLATFORM_SECTION_ENUM } from "@/utils/enums";
import type { IChatMessage } from "@/types/(my-space)/chat.interface";
import { CardAction, CardTile, RichCard } from "./ChatCard";
import {
  chatShortcutActionFor,
  chatShortcutFor,
  chatShortcutKindFor,
} from "../_lib/chat-shortcuts";
import { shortcutItemStatus, shortcutSubtitle } from "../_lib/shortcut-format";
import { shortcutTarget } from "../_lib/shortcut-target";

/**
 * A Shortcut message: a module, one of the sender's items (leave, expense,
 * event…) or a form to open. Where it leads depends on who is looking — the
 * owner gets their own page, an approver the company one, anyone else a toast.
 */
export const ShortcutCard = ({ message: m, isMine }: { message: IChatMessage; isMine: boolean }) => {
  const { t } = useTranslation("chat");
  const navigate = useNavigate();
  const locale = useLocale();
  const myUserId = useAppSelector(selectUser).user?.id;
  const { can } = usePermissions();
  const sc = m.metadata?.shortcut;

  const open = () => {
    const target = shortcutTarget(sc, {
      myUserId,
      canViewCompany: (section: PLATFORM_SECTION_ENUM) =>
        can(section, "view", PermissionLevelEnum.COMPANY),
    });
    if (target.kind === "path") navigate(target.path);
    else if (target.kind === "no_access") toast.error(t("shortcut_card.no_access"));
  };

  const arrow = (
    <CardAction isMine={isMine}>
      <ChevronRight className="rtl-flip" />
    </CardAction>
  );

  const entity = sc?.entity;
  const kind = chatShortcutKindFor(entity?.type);
  if (entity && kind) {
    const subtitle = shortcutSubtitle(entity, locale);
    return (
      <RichCard
        isMine={isMine}
        onClick={open}
        leading={
          <CardTile tone={kind.tone} isMine={isMine}>
            <kind.icon />
          </CardTile>
        }
        title={entity.title || kind.label(t)}
        subtitle={subtitle || kind.label(t)}
        meta={
          entity.status ? (
            <Badge variant={statusToVariant(entity.status)}>
              {shortcutItemStatus(t, entity)}
            </Badge>
          ) : null
        }
        label={t("shortcut_card.open_item", { kind: kind.label(t), title: entity.title })}
        action={arrow}
      />
    );
  }

  const action = chatShortcutActionFor(sc?.action);
  if (action) {
    return (
      <RichCard
        isMine={isMine}
        onClick={open}
        leading={
          <CardTile tone="primary" isMine={isMine}>
            <action.icon />
          </CardTile>
        }
        title={action.label(t)}
        subtitle={
          // Clock in/out opens the dashboard's clock button, not a form.
          action.action === "clock_in"
            ? t("shortcut_card.opens_clock")
            : t("shortcut_card.opens_form")
        }
        action={arrow}
      />
    );
  }

  const shortcut = chatShortcutFor(sc?.route);
  const Icon = shortcut?.icon ?? ExternalLink;
  // The reader's language, not the label the sender saw.
  const label = shortcut ? shortcut.label(t) : sc?.label || t("preview.shortcut");
  return (
    <RichCard
      isMine={isMine}
      onClick={open}
      disabled={!shortcut}
      leading={
        <CardTile tone="primary" isMine={isMine}>
          <Icon />
        </CardTile>
      }
      title={label}
      subtitle={t("card.opens_in_app")}
      action={arrow}
    />
  );
};
